import { CSSStyles } from '@/modules/core/css/types/CSSStyles';
import { Campus } from '@/modules/core/data/types/Campus';
import { Customer } from '@/modules/core/data/types/Customer';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { ModSLS } from '@/modules/core/data/types/ModSLS';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import { StudentSolutionGroup } from '@/modules/core/data/types/StudentSolutionGroup';
import { StudentSolutionsGroupByType } from '@/modules/core/data/types/StudentSolutionsGroupByType';
import deepmerge from 'deepmerge';
import { TFunction } from 'i18next';
import filter from 'lodash.filter';
import kebabCase from 'lodash.kebabcase';
import map from 'lodash.map';
import orderBy from 'lodash.orderby';
import { computeStudentSolutionGroups } from '../solutions/studentSolutionGroup';
import {
  generateLogoHTML,
  generateStudentSolutionContactHTML,
  generateStudentSolutionDescriptionHTML,
} from './SLS-templates';

export type PartnerColumnMetadata = {
  metadata?: {
    classes?: string;
    styles?: CSSStyles; // CSS styles - React style object - See https://reactjs.org/docs/dom-elements.html#style
  };
  content: string; // HTML
}

export type Partner = {
  // ref: string; // XXX Doesn't exist on SLS for now, might be added later
  name: string; // XXX For compatibility with SLS, unused in TFP
  columns: Array<PartnerColumnMetadata>;
}

export type PartnersGroup = {
  ref: string;
  name: string; // XXX For compatibility with SLS, unused in TFP
  label: string;
  partners: Partner[];
}

export type PartnersGroups = PartnersGroup[]

export const convertThemeToSLSFormat = (theme: CustomerTheme): GenericObject => {
  return {
    primaryColor: theme?.primaryColor,
    primaryAlternativeColor: theme?.secondaryColor, // SLS uses "primaryAlternativeColor" (equivalent)
    secondaryColor: theme?.backgroundColor,
    font: theme?.fonts,
    borrowerSignUpLinkScenario: {
      advicesGuideTextColor: theme?.primaryColor,
    },
  };
};

export const buildPartnersGroup = (t: TFunction, ref: string, label: string, studentSolutions: StudentSolution[]): PartnersGroup => {
  return {
    ref,
    name: ref,
    label,
    partners: map(studentSolutions, (studentSolution: StudentSolution, j: number): Partner => {
      return {
        // ref: studentSolution.ref,
        name: studentSolution.ref,
        columns: [
          {
            content: studentSolution.logo ? generateLogoHTML(studentSolution.logo) : '',
          },
          {
            content: generateStudentSolutionDescriptionHTML(t, studentSolution),
          },
          {
            content: generateStudentSolutionContactHTML(studentSolution),
          },
        ],
      };
    }),
  };
};

/**
 * A TFP studentSolution corresponds to a SLS partner
 *
 * XXX A "group" is a generic entity meant to regroup solutions together
 * StudentSolutions are grouped by groups, depending on the groupType (campus|program)
 * That way, each group displays a list of solutions that are related to that group
 *
 * @param t
 * @param {StudentSolution[]} studentSolutions
 * @param {Campus[]} campuses
 * @param {StudentSolutionsGroupByType} partnersGroupsType
 * @return {PartnersGroups}
 */
export const convertStudentSolutionsToSLSPartners = (
  t: TFunction,
  studentSolutions: StudentSolution[],
  campuses: Campus[],
  partnersGroupsType: StudentSolutionsGroupByType,
): PartnersGroups => {
  const partnersGroups: PartnersGroups = [];
  let studentSolutionGroups: StudentSolutionGroup[];
  // Only solutions where isVisibleSLS=true should be displayed on SLS
  const studentSolutionsToDisplay: StudentSolution[] = filter(studentSolutions, (studentSolution: StudentSolution) => studentSolution.isVisibleSLS);

  // Use empty context, used to resolve the displayPriorityScore of each group, when grouping by EducationalProgram (ordered by selected programs)
  // But it's not useful here as we don't really care about ordering programs this way in SLS, they'll be ordered by label and that's it
  const context: StudentSolutionContext = {} as StudentSolutionContext;

  // Resolve groups of studentSolutions, and ignoring the default group in any case
  if (partnersGroupsType === 'Campus') {
    studentSolutionGroups = computeStudentSolutionGroups(studentSolutionsToDisplay, context, 'Campus', campuses, 'Aucun', false, true);
  } else {
    studentSolutionGroups = computeStudentSolutionGroups(studentSolutionsToDisplay, context, 'EducationalProgram', campuses, 'Aucun', false, true);
    studentSolutionGroups = orderBy(studentSolutionGroups, ['displayPriorityScore', 'label'], ['desc', 'asc']);
  }

  // XXX [Dirty fix] - Handles special edge case when ALL solutions were not explicitly linked to campus/programs and no group were generated
  //  Needs to be reimplemented - See https://app.asana.com/0/inbox/584852631485915
  //  Basically, this only handles one particular edge case but not all, it's a quick fix.
  //  If 2 solutions have "isVisibleSLS=true" but only one defines a relationship with a program, then only one will be displayed with this fix.
  if (studentSolutionGroups?.length === 0) {
    studentSolutionGroups = [
      {
        id: 'fallbackDefault',
        ref: 'fallbackDefault',
        label: t('simulatorPage.fallbackDefaultGroup.label', 'Tous'),
        solutions: studentSolutionsToDisplay,
        displayPriorityScore: 1,
        isDefaultGroup: true,
        __typename: partnersGroupsType,
      },
    ];
  }

  // For each group that contains any solution (groups without any solutions are automatically filtered out)
  map(studentSolutionGroups, (studentSolutionGroup: StudentSolutionGroup) => {
    partnersGroups.push(buildPartnersGroup(
      t,
      kebabCase(studentSolutionGroup.label),
      studentSolutionGroup.label,
      studentSolutionGroup.solutions,
    ));
  });

  return partnersGroups;
};

/**
 * Inject default settings based on the current environment, if those settings aren't defined yet.
 * Basically, the goal is to dynamically provide the good endpoints based on the environment,
 * thus avoid storing them in the DB or config file.
 *
 * @param t
 * @param simulatorSettings
 * @returns {*}
 * @private
 */
const _applyDefaultSimulatorSettings = (t, simulatorSettings): GenericObject => {
  const stage = process.env.NEXT_PUBLIC_APP_STAGE;
  let saveFeedbackEndpoint;
  let updateFeedbackEndpoint;
  let saveSimulationEndpoint;

  if (stage === 'development') {
    saveFeedbackEndpoint = 'http://localhost:5000/feedback/save';
    updateFeedbackEndpoint = 'http://localhost:5000/feedback/update';
    saveSimulationEndpoint = 'http://localhost:5000/simulation/save';
  } else if (stage === 'staging') {
    saveFeedbackEndpoint = 'https://staging.sls.unly.org/api/feedback/save';
    updateFeedbackEndpoint = 'https://staging.sls.unly.org/api/feedback/update';
    saveSimulationEndpoint = 'https://staging.sls.unly.org/api/simulation/save';
  } else { // XXX If neither dev nor staging then consider production env
    saveFeedbackEndpoint = 'https://sls.unly.org/api/feedback/save';
    updateFeedbackEndpoint = 'https://sls.unly.org/api/feedback/update';
    saveSimulationEndpoint = 'https://sls.unly.org/api/simulation/save';
  }

  const defaultEnvironmentSettings = {
    modules: {
      core: {
        simulator: {
          inputs: { // Disable useless fields
            usage: {
              hidden: true,
            },
            institution: {
              hidden: true,
            },
            mostRecentCompletedDegree: {
              hidden: true,
            },
            hasFrenchNationality: {
              hidden: true,
            },
            hasVoucher: {
              hidden: true,
            },
            isNotBankingForbidden: {
              hidden: true,
            },
            isNotFinanciallyExposed: {
              hidden: true,
            },
          },
          mostRecentCompletedDegrees: [
            {
              value: 'none',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.none', `Aucun diplôme`),
            },
            {
              value: 'cap-bep-ter',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.cap-bep-ter', `CAP-BEP Tertiaire`),
            },
            {
              value: 'cap-bep-ind',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.cap-bep-ind', `CAP-BEP Industriel`),
            },
            {
              value: 'bac-pro-tec-ter',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac-pro-tec-ter', `Bac Pro / Techno tertiaire`),
            },
            {
              value: 'bac-pro-tec-ind',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac-pro-tec-ind', `Bac Pro / Techno industriel`),
            },
            {
              value: 'bac-gen',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac-gen', `Bac Général`),
            },
            {
              value: 'bac+2-ter',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+2-ter', `Bac+2 Tertiaire (DEUG, BTS, DUT, DEUST)`),
            },
            {
              value: 'bac+2-ind',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+2-ind', `Bac+2 Industriel (DEUG, BTS, DUT, DEUST)`),
            },
            {
              value: 'bac+2/3-soc',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+2/3-soc', `Bac+2/3 Santé social (DEUG, BTS, DUT, DEUST, Licence ou équivalent)`),
            },
            {
              value: 'pro-lic',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.pro-lic', `Licence Professionnelle`),
            },
            {
              value: 'bac+3/4-lit',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+3/4-lit', `Bac+3/4 LSH, gestion, droit (Licence, Bachelor ou équivalent)`),
            },
            {
              value: 'bac+3/4-sci',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+3/4-sci', `Bac+3/4 Maths, sc et tech. STAPS (Licence, Bachelor ou équivalent)`),
            },
            {
              value: 'bac+5-lit',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+5-lit', `Bac+5 LSH, gestion, droit (Master, DEA, DESS)`),
            },
            {
              value: 'bac+5-sci',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+5-sci', `Bac+5 Maths, sc et tech. STAPS (Master, DEA, DESS)`),
            },
            {
              value: 'bac+5-com',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+5-com', `Bac+5 École de commerce`),
            },
            {
              value: 'bac+5-eng',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+5-eng', `ac+5 École d'ingénieurs`),
            },
            {
              value: 'bac+7-doc-d',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.mostRecentCompletedDegrees.bac+7-doc-d', `Bac+7 Doctorat`),
            },
          ],
          expectedDegrees: [
            {
              value: 'bac+2-ter',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+2-ter', `Bac+2 Tertiaire (BTS, DUT)`),
              estimatedMonthlySalary: 1200,
              firstJobSearchDuration: 4,
            },
            {
              value: 'bac+2-ind',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+2-ind', `Bac+2 Industriel (BTS, DUT)`),
              estimatedMonthlySalary: 1350,
              firstJobSearchDuration: 4,
            },
            {
              value: 'bac+2/3-soc',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+2/3-soc', `Bac+2/3 Santé social (BTS, DUT, DE, Licence ou équivalent)`),
              estimatedMonthlySalary: 1550,
              firstJobSearchDuration: 3,
            },
            {
              value: 'pro-lic',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.pro-lic', `Licence Professionnelle`),
              estimatedMonthlySalary: 1200,
              firstJobSearchDuration: 4,
            },
            {
              value: 'bac+3/4-lit',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+3/4-lit', `Bac+3/4 LSH, gestion, droit (Licence, Bachelor ou équivalent)`),
              estimatedMonthlySalary: 1200,
              firstJobSearchDuration: 4,
            },
            {
              value: 'bac+3/4-sci',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+3/4-sci', `Bac+3/4 Maths, sc et tech. STAPS (Licence, Bachelor ou équivalent)`),
              estimatedMonthlySalary: 1410,
              firstJobSearchDuration: 4,
            },
            {
              value: 'bac+5-lit',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+5-lit', `Bac+5 LSH, gestion, droit`),
              estimatedMonthlySalary: 1470,
              firstJobSearchDuration: 3,
            },
            {
              value: 'bac+5-sci',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+5-sci', `Bac+5 Maths, sc et tech. STAPS`),
              estimatedMonthlySalary: 1700,
              firstJobSearchDuration: 3,
            },
            {
              value: 'bac+5-com',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+5-com', `Bac+5 École de commerce`),
              estimatedMonthlySalary: 2000,
              firstJobSearchDuration: 3,
            },
            {
              value: 'bac+5-eng',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+5-eng', `Bac+5 École d'ingénieurs`),
              estimatedMonthlySalary: 2000,
              firstJobSearchDuration: 3,
            },
            {
              value: 'bac+7-doc-d',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.expectedDegrees.bac+7-doc-d', `Bac+7 Doctorat`),
              estimatedMonthlySalary: 2000,
              firstJobSearchDuration: 3,
            },
          ],
          usages: [
            {
              value: 'studies',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.usages.studies', `Frais de scolarité`),
            },
            {
              value: 'everyday-life',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.usages.everyday-life', `Vie quotidienne`),
            },
            {
              value: 'computer',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.usages.computer', `Ordinateur`),
            },
            {
              value: 'internship',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.usages.internship', `Stage / Échange à l'étranger`),
            },
            {
              value: 'school-materials',
              label: t('simulatorPage.SLSSettings.modules.core.simulator.usages.school-materials', `Matériel scolaire`),
            },
          ],
          // recommended: {
          //   interestRate: 0.8, // TODO Custom interest rate based on best available partnership?
          // },
        },
      },
      feedback: {
        saveFeedback: {
          endpoint: saveFeedbackEndpoint,
        },
        updateFeedback: {
          endpoint: updateFeedbackEndpoint,
        },
        onFeedbackChange: (props): void => { // XXX Defined in simulator.tsx
          // @ts-ignore
          window.onSLSFeedbackChange(props);
        },
      },
      saveSimulation: {
        endpoint: saveSimulationEndpoint,
      },
    },
  };

  return deepmerge(defaultEnvironmentSettings, simulatorSettings);
};

/**
 * Compiles a specific version of the settings, based on the Simulator application format
 * Also, apply default URL for endpoints based on the runtime environment
 *
 * @param t
 * @param customer
 * @param modSLS
 * @param studentSolutions
 * @param campuses
 * @param educationalPrograms
 * @param lang
 * @param release
 * @param theme
 * @returns {*}
 */
export const resolveSimulatorSettings = (
  t: TFunction,
  customer: Customer,
  modSLS: ModSLS,
  studentSolutions: StudentSolution[],
  campuses: Campus[],
  educationalPrograms: EducationalProgram[],
  lang: string,
  release: string,
  theme: CustomerTheme,
): GenericObject => {
  const partnersGroupsType: StudentSolutionsGroupByType = modSLS?.studentSolutionsGroupsType || 'Campus';
  const partnersGroupsLabel = partnersGroupsType === 'Campus' ?
    t('simulatorPage.SLSSettings.modules.core.simulator.partnersGroupLabel.campus', `Campus`) :
    t('simulatorPage.SLSSettings.modules.core.simulator.partnersGroupLabel.program', `Program`);
  const partnersGroups = convertStudentSolutionsToSLSPartners(t, studentSolutions, campuses, partnersGroupsType);

  // TODO tests ? > Not sure since the format can change often, and SLS already test the inputs and warn about what's wrong
  return _applyDefaultSimulatorSettings(t, {
    name: customer?.ref,
    organisation: {
      name: customer?.ref, // XXX We don't handle the concept of Organisation in v4, yet
    },
    modules: {
      core: {
        simulator: {
          inputs: { // Disable useless fields
            usage: {
              hidden: true,
            },
            institution: {
              hidden: true,
            },
            mostRecentCompletedDegree: {
              hidden: true,
            },
            hasFrenchNationality: {
              hidden: true,
            },
            hasVoucher: {
              hidden: true,
            },
            isNotBankingForbidden: {
              hidden: true,
            },
            isNotFinanciallyExposed: {
              hidden: true,
            },
          },
          recommended: {
            interestRate: modSLS.recommendedInterestRate,
          },
          partnersGroupsLabel,
        },
      },
      chatbotAdvisor: {
        enabled: true,
        label: t('simulatorPage.SLSSettings.modules.chatbotAdvisor.label', 'le chatbot'),
        link: {
          url: `/${lang}/chatbot`,
          target: '_blank',
        },
      },
      formInitialValues: {
        values: {
          institution: customer?.label,
        },
      },
      theme: convertThemeToSLSFormat(theme),
      i18n: {
        lang,
        version: process.env.NEXT_PUBLIC_APP_STAGE === 'production' ? `v${release}` : 'latest', // In production, use the officially release
      },
    },
    scenarios: {
      simulatorResultsFeedback: {
        borrowingAssistance: {
          customTitleWording: modSLS?.borrowingAssistanceTitle || '',
          // XXX customParagraphWording doesn't handle HTML, so we strip the container <p></p> and return null if not truthy
          customParagraphWording: (modSLS?.borrowingAssistanceDescription || '').replace('<p>', '').replace('</p>', ''),
        },
      },
    },
    partnersGroups,
  });
};
